/**
 * Recognise a stale-deployment chunk failure.
 *
 * When a new build ships, the previous build's JS chunks are deleted. A browser
 * holding the OLD HTML then asks for a chunk that no longer exists and React
 * throws
 *
 *     Failed to load chunk /_next/static/chunks/0y_7we863hdb4.js from module 964893
 *
 * which every route error boundary catches and renders as "Something went
 * wrong". Observed 11 times in one review window after a rebuild.
 *
 * Two things make this worth special-casing rather than leaving generic:
 *
 *  1. The boundary's `Try again` calls `reset()`, which re-renders the SAME
 *     stale document and fails identically. The control on screen cannot fix
 *     the problem it is offered for, so the user retries until they give up.
 *  2. The real remedy — reload the page — is trivial, but nothing tells them.
 *     "Something went wrong" gives no reason to think a refresh would help.
 *
 * Deliberately NOT an automatic reload. An auto-reload on a render error is a
 * reload loop whenever the cause is something else, and it can discard work the
 * user has typed. The decision stays with them; this only makes it an informed
 * one.
 */
export function isStaleChunkError(error: unknown): boolean {
  if (!error) return false;

  const message =
    error instanceof Error
      ? `${error.name} ${error.message}`
      : typeof error === 'string'
        ? error
        : '';

  if (!message) return false;

  // Next/webpack phrase it differently across versions; match the stable parts.
  return (
    /ChunkLoadError/i.test(message) ||
    /Loading chunk \S+ failed/i.test(message) ||
    /Failed to load chunk/i.test(message)
  );
}
